import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChapterRuntime, CompactTimingMap, resolveTimingOrdinal } from '@/utils/ordinalSync';
import { getMountedChapterIndex } from '@/utils/readerDomRegistry';

const PROGRESS_OVERLAY_CLASS = 'reader-progress-overlay';
const PROGRESS_OVERLAY_RECT_CLASS = 'reader-progress-overlay-rect';
const LINE_MERGE_VERTICAL_TOLERANCE_PX = 1.5;

export interface WordSyncRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface MutableLine {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface CachedOrdinalGeometry {
  rects: WordSyncRect[];
  bounds: WordSyncRect | null;
}

interface CachedBlockGeometry {
  startOrdinal: number;
  endOrdinal: number;
  builtThroughOrdinal: number;
  lines: MutableLine[];
  progressRectsByOrdinal: Map<number, WordSyncRect[]>;
  geometryByOrdinal: Map<number, CachedOrdinalGeometry>;
  lineRectByOrdinal: Map<number, WordSyncRect>;
}

interface ObservedSize {
  width: number;
  height: number;
}

interface UseWordSyncArgs {
  chapterId: string | null;
  runtime: ChapterRuntime | null;
  containerRef: RefObject<HTMLElement | null>;
  documentVersion: number;
  enabled: boolean;
}

interface UseWordSyncResult {
  clear: () => void;
  getChapterRoot: () => HTMLElement | null;
  getTokenRectForTimingIndex: (index: number) => WordSyncRect | null;
  isIndexReady: boolean;
  hasIntegrityMismatch: boolean;
  layoutVersion: number;
  paintTimingIndex: (index: number) => void;
}

export const resolveWordSyncTimingIndex = (timings: CompactTimingMap, currentMs: number): number =>
  resolveTimingOrdinal(timings.startsMs, timings.endsMs, currentMs);

const mergeRectIntoLines = (lines: MutableLine[], rect: {
  left: number;
  right: number;
  top: number;
  bottom: number;
}) => {
  const line = lines.find(
    (candidate) =>
      rect.top <= candidate.bottom + LINE_MERGE_VERTICAL_TOLERANCE_PX &&
      rect.bottom >= candidate.top - LINE_MERGE_VERTICAL_TOLERANCE_PX,
  );
  if (!line) {
    lines.push({ ...rect });
    return;
  }
  line.top = Math.min(line.top, rect.top);
  line.bottom = Math.max(line.bottom, rect.bottom);
  line.left = Math.min(line.left, rect.left);
  line.right = Math.max(line.right, rect.right);
};

const linesToRects = (lines: MutableLine[]): WordSyncRect[] =>
  lines.map((line) => ({
    left: line.left,
    top: line.top,
    width: Math.max(0, line.right - line.left),
    height: Math.max(0, line.bottom - line.top),
  }));

const toBounds = (rects: WordSyncRect[]): WordSyncRect | null => {
  if (rects.length === 0) return null;
  const left = Math.min(...rects.map((rect) => rect.left));
  const top = Math.min(...rects.map((rect) => rect.top));
  const right = Math.max(...rects.map((rect) => rect.left + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.top + rect.height));
  return { left, top, width: right - left, height: bottom - top };
};

export const useWordSync = ({
  chapterId,
  runtime,
  containerRef,
  documentVersion,
  enabled,
}: UseWordSyncArgs): UseWordSyncResult => {
  const activeOrdinalRef = useRef(-1);
  const activeBlockRef = useRef<number | null>(null);
  const desiredProgressRef = useRef<{ chapterId: string; ordinal: number } | null>(null);
  const mismatchWarningRef = useRef<string | null>(null);
  const resizeFrameRef = useRef<number | null>(null);
  const chapterRootRef = useRef<HTMLElement | null>(null);
  const progressOverlayRef = useRef<HTMLDivElement | null>(null);
  const progressOverlayRectNodesRef = useRef<HTMLDivElement[]>([]);
  const renderedProgressRectsRef = useRef<Array<WordSyncRect | null>>([]);
  const originalChapterRootInlinePositionRef = useRef<string | null>(null);
  const blockGeometryRef = useRef<Map<number, CachedBlockGeometry>>(new Map());
  const lastObservedSizeRef = useRef<Map<Element, ObservedSize>>(new Map());
  const [layoutVersion, setLayoutVersion] = useState(0);
  const index = chapterId ? getMountedChapterIndex(chapterId) : null;
  const isIndexReady = Boolean(
    enabled &&
    runtime &&
    index &&
    index.segmentsByOrdinal.length === runtime.tokenMap.tokens.length &&
    runtime.timings.startsMs.length === runtime.tokenMap.tokens.length,
  );
  const hasIntegrityMismatch = Boolean(enabled && runtime && index && !isIndexReady);

  useEffect(() => {
    if (!enabled || !runtime || !index || !chapterId) {
      mismatchWarningRef.current = null;
      return;
    }
    const key = `${chapterId}:${index.segmentsByOrdinal.length}:${runtime.tokenMap.tokens.length}:${runtime.timings.startsMs.length}`;
    if (!isIndexReady && mismatchWarningRef.current !== key) {
      mismatchWarningRef.current = key;
      console.warn(`[reader] Ordinal DOM/token/timing counts do not agree for chapter '${chapterId}'; synchronization is disabled.`);
    }
  }, [chapterId, enabled, index, isIndexReady, runtime]);

  const clearRenderedProgressRects = useCallback(() => {
    progressOverlayRectNodesRef.current.forEach((node) => node.remove());
    progressOverlayRectNodesRef.current = [];
    renderedProgressRectsRef.current = [];
  }, []);

  const clearVisualProgress = useCallback(() => {
    clearRenderedProgressRects();
    activeOrdinalRef.current = -1;
    activeBlockRef.current = null;
  }, [clearRenderedProgressRects]);

  const clear = useCallback(() => {
    desiredProgressRef.current = null;
    clearVisualProgress();
  }, [clearVisualProgress]);

  const teardownProgressOverlay = useCallback(() => {
    clearVisualProgress();
    progressOverlayRef.current?.remove();
    progressOverlayRef.current = null;

    const chapterRoot = chapterRootRef.current;
    chapterRoot?.classList.remove('reader-progress-layer-host');
    if (chapterRoot?.dataset.readerWordSyncPositionPatched === 'true') {
      const originalPosition = originalChapterRootInlinePositionRef.current;
      if (originalPosition) {
        chapterRoot.style.position = originalPosition;
      } else {
        chapterRoot.style.removeProperty('position');
      }
      delete chapterRoot.dataset.readerWordSyncPositionPatched;
    }
    chapterRootRef.current = null;
    originalChapterRootInlinePositionRef.current = null;
  }, [clearVisualProgress]);

  const ensureProgressOverlay = useCallback((chapterRoot: HTMLElement): HTMLDivElement => {
    if (progressOverlayRef.current && chapterRootRef.current === chapterRoot) {
      return progressOverlayRef.current;
    }
    teardownProgressOverlay();

    if (window.getComputedStyle(chapterRoot).position === 'static') {
      originalChapterRootInlinePositionRef.current = chapterRoot.style.position || '';
      chapterRoot.style.position = 'relative';
      chapterRoot.dataset.readerWordSyncPositionPatched = 'true';
    } else {
      originalChapterRootInlinePositionRef.current = null;
    }

    const overlay = document.createElement('div');
    overlay.className = PROGRESS_OVERLAY_CLASS;
    chapterRoot.classList.add('reader-progress-layer-host');
    chapterRoot.appendChild(overlay);
    chapterRootRef.current = chapterRoot;
    progressOverlayRef.current = overlay;
    return overlay;
  }, [teardownProgressOverlay]);

  const drawProgressOverlay = useCallback((rects: WordSyncRect[]) => {
    const overlay = progressOverlayRef.current;
    if (!overlay) return;

    const nodes = progressOverlayRectNodesRef.current;
    const renderedRects = renderedProgressRectsRef.current;
    while (nodes.length > rects.length) {
      nodes.pop()?.remove();
      renderedRects.pop();
    }
    rects.forEach((rect, rectIndex) => {
      let node = nodes[rectIndex];
      if (!node) {
        node = document.createElement('div');
        node.className = PROGRESS_OVERLAY_RECT_CLASS;
        nodes[rectIndex] = node;
        overlay.appendChild(node);
      }
      const previous = renderedRects[rectIndex];
      if (!previous || previous.left !== rect.left) node.style.left = `${rect.left}px`;
      if (!previous || previous.top !== rect.top) node.style.top = `${rect.top}px`;
      if (!previous || previous.width !== rect.width) node.style.width = `${rect.width}px`;
      if (!previous || previous.height !== rect.height) node.style.height = `${rect.height}px`;
      renderedRects[rectIndex] = rect;
    });
  }, []);

  const ensureBlockGeometryComputed = useCallback((ordinal: number): CachedBlockGeometry | null => {
    const currentIndex = chapterId ? getMountedChapterIndex(chapterId) : null;
    if (!currentIndex || !runtime || ordinal < 0 || ordinal >= runtime.tokenMap.blocks.length) return null;

    const block = runtime.tokenMap.blocks[ordinal];
    const existing = blockGeometryRef.current.get(block);
    if (existing) return existing;

    let startOrdinal = ordinal;
    while (startOrdinal > 0 && runtime.tokenMap.blocks[startOrdinal - 1] === block) startOrdinal -= 1;
    let endOrdinal = ordinal;
    while (
      endOrdinal + 1 < runtime.tokenMap.blocks.length &&
      runtime.tokenMap.blocks[endOrdinal + 1] === block
    ) endOrdinal += 1;

    const rootRect = currentIndex.root.getBoundingClientRect();
    const geometryByOrdinal = new Map<number, CachedOrdinalGeometry>();
    const fullLineGeometry: MutableLine[] = [];
    for (let currentOrdinal = startOrdinal; currentOrdinal <= endOrdinal; currentOrdinal += 1) {
      const rects = (currentIndex.segmentsByOrdinal[currentOrdinal] ?? []).flatMap((element) =>
        Array.from(element.getClientRects())
          .filter((rect) => rect.width > 0 && rect.height > 0)
          .map((rect) => ({
            left: rect.left - rootRect.left,
            top: rect.top - rootRect.top,
            width: rect.width,
            height: rect.height,
          })),
      );
      geometryByOrdinal.set(currentOrdinal, { rects, bounds: toBounds(rects) });
      rects.forEach((rect) => mergeRectIntoLines(fullLineGeometry, {
        left: rect.left,
        right: rect.left + rect.width,
        top: rect.top,
        bottom: rect.top + rect.height,
      }));
    }

    const fullLineRects = linesToRects(fullLineGeometry);
    const lineRectByOrdinal = new Map<number, WordSyncRect>();
    geometryByOrdinal.forEach((geometry, currentOrdinal) => {
      const anchorRect = geometry.rects[geometry.rects.length - 1];
      if (!anchorRect) return;
      const anchorBottom = anchorRect.top + anchorRect.height;
      const lineRect = fullLineRects.find((candidate) =>
        anchorRect.top <= candidate.top + candidate.height + LINE_MERGE_VERTICAL_TOLERANCE_PX &&
        anchorBottom >= candidate.top - LINE_MERGE_VERTICAL_TOLERANCE_PX,
      );
      if (lineRect) lineRectByOrdinal.set(currentOrdinal, lineRect);
    });

    const geometry: CachedBlockGeometry = {
      startOrdinal,
      endOrdinal,
      builtThroughOrdinal: startOrdinal - 1,
      lines: [],
      progressRectsByOrdinal: new Map(),
      geometryByOrdinal,
      lineRectByOrdinal,
    };
    blockGeometryRef.current.set(block, geometry);
    return geometry;
  }, [chapterId, runtime]);

  const ensureBlockProgressRects = useCallback((geometry: CachedBlockGeometry, ordinal: number): WordSyncRect[] | null => {
    const cached = geometry.progressRectsByOrdinal.get(ordinal);
    if (cached) return cached;

    for (
      let currentOrdinal = geometry.builtThroughOrdinal + 1;
      currentOrdinal <= ordinal && currentOrdinal <= geometry.endOrdinal;
      currentOrdinal += 1
    ) {
      geometry.geometryByOrdinal.get(currentOrdinal)?.rects.forEach((rect) => {
        mergeRectIntoLines(geometry.lines, {
          left: rect.left,
          right: rect.left + rect.width,
          top: rect.top,
          bottom: rect.top + rect.height,
        });
      });
      geometry.progressRectsByOrdinal.set(currentOrdinal, linesToRects(geometry.lines));
      geometry.builtThroughOrdinal = currentOrdinal;
    }
    return geometry.progressRectsByOrdinal.get(ordinal) ?? null;
  }, []);

  const paintTimingIndex = useCallback((ordinal: number) => {
    const currentIndex = chapterId ? getMountedChapterIndex(chapterId) : null;
    if (!enabled || !chapterId || !currentIndex || !runtime || ordinal < 0 || ordinal >= runtime.timings.startsMs.length) {
      clear();
      return;
    }
    const block = runtime.tokenMap.blocks[ordinal];
    if (activeOrdinalRef.current === ordinal && activeBlockRef.current === block) return;

    ensureProgressOverlay(currentIndex.root);
    if (activeBlockRef.current !== null && activeBlockRef.current !== block) {
      clearRenderedProgressRects();
    }
    const geometry = ensureBlockGeometryComputed(ordinal);
    const progressRects = geometry ? ensureBlockProgressRects(geometry, ordinal) : null;
    if (!progressRects?.length) {
      clear();
      return;
    }
    drawProgressOverlay(progressRects);
    activeOrdinalRef.current = ordinal;
    activeBlockRef.current = block;
    desiredProgressRef.current = { chapterId, ordinal };
  }, [
    chapterId,
    clear,
    clearRenderedProgressRects,
    drawProgressOverlay,
    enabled,
    ensureBlockGeometryComputed,
    ensureBlockProgressRects,
    ensureProgressOverlay,
    runtime,
  ]);

  const getChapterRoot = useCallback(
    () => chapterId ? getMountedChapterIndex(chapterId)?.root ?? null : null,
    [chapterId],
  );

  const getTokenRectForTimingIndex = useCallback((ordinal: number): WordSyncRect | null => {
    const geometry = ensureBlockGeometryComputed(ordinal);
    return geometry?.lineRectByOrdinal.get(ordinal) ?? geometry?.geometryByOrdinal.get(ordinal)?.bounds ?? null;
  }, [ensureBlockGeometryComputed]);

  const invalidateGeometry = useCallback(() => {
    blockGeometryRef.current = new Map();
    setLayoutVersion((value) => value + 1);
    const activeOrdinal = activeOrdinalRef.current;
    if (activeOrdinal >= 0) {
      activeOrdinalRef.current = -1;
      activeBlockRef.current = null;
      paintTimingIndex(activeOrdinal);
    }
  }, [paintTimingIndex]);

  useEffect(() => {
    const desiredOrdinal = desiredProgressRef.current?.chapterId === chapterId
      ? desiredProgressRef.current.ordinal
      : -1;
    if (desiredProgressRef.current && desiredProgressRef.current.chapterId !== chapterId) {
      desiredProgressRef.current = null;
    }
    teardownProgressOverlay();
    blockGeometryRef.current = new Map();
    lastObservedSizeRef.current = new Map();
    if (!isIndexReady || !chapterId) return;

    const currentIndex = getMountedChapterIndex(chapterId);
    const container = containerRef.current;
    if (!currentIndex || !container) return;
    ensureProgressOverlay(currentIndex.root);
    if (desiredOrdinal >= 0) paintTimingIndex(desiredOrdinal);

    const scheduleInvalidation = () => {
      if (resizeFrameRef.current !== null) return;
      resizeFrameRef.current = requestAnimationFrame(() => {
        resizeFrameRef.current = null;
        invalidateGeometry();
      });
    };
    const handleResize = (entries: ResizeObserverEntry[]) => {
      let sizeChanged = false;
      entries.forEach((entry) => {
        const next = { width: entry.contentRect.width, height: entry.contentRect.height };
        const previous = lastObservedSizeRef.current.get(entry.target);
        if (!previous || Math.abs(previous.width - next.width) > 0.5 || Math.abs(previous.height - next.height) > 0.5) {
          sizeChanged = true;
        }
        lastObservedSizeRef.current.set(entry.target, next);
      });
      if (sizeChanged) scheduleInvalidation();
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(handleResize);
    observer?.observe(currentIndex.root);
    observer?.observe(container);
    window.addEventListener('resize', scheduleInvalidation);
    window.addEventListener('orientationchange', scheduleInvalidation);
    setLayoutVersion((value) => value + 1);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', scheduleInvalidation);
      window.removeEventListener('orientationchange', scheduleInvalidation);
      if (resizeFrameRef.current !== null) {
        cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
      teardownProgressOverlay();
      blockGeometryRef.current = new Map();
      lastObservedSizeRef.current = new Map();
    };
  }, [
    chapterId,
    containerRef,
    documentVersion,
    ensureProgressOverlay,
    invalidateGeometry,
    isIndexReady,
    paintTimingIndex,
    teardownProgressOverlay,
  ]);

  return useMemo(() => ({
    clear,
    getChapterRoot,
    getTokenRectForTimingIndex,
    isIndexReady,
    hasIntegrityMismatch,
    layoutVersion,
    paintTimingIndex,
  }), [
    clear,
    getChapterRoot,
    getTokenRectForTimingIndex,
    isIndexReady,
    hasIntegrityMismatch,
    layoutVersion,
    paintTimingIndex,
  ]);
};
